import React, { useState, useRef, useEffect, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Search } from 'lucide-react'
import * as LucideIcons from 'lucide-react'

// ── Curated list of IT/business-relevant icons ──

const DOMAIN_ICONS = [
    'Shield', 'Server', 'Database', 'Cloud', 'Network', 'Lock',
    'Monitor', 'Cpu', 'HardDrive', 'Wifi', 'Globe', 'Code',
    'Terminal', 'Layout', 'Layers', 'Settings', 'Wrench', 'Cog',
    'BarChart3', 'PieChart', 'TrendingUp', 'Activity', 'Gauge',
    'Users', 'UserCog', 'Building2', 'Briefcase', 'FileText',
    'FolderOpen', 'ClipboardList', 'CheckSquare', 'ListChecks',
    'Zap', 'Rocket', 'Target', 'Award', 'Star',
    'Eye', 'Search', 'Key', 'ShieldCheck', 'ShieldAlert',
    'Bug', 'AlertTriangle', 'Info', 'HelpCircle',
    'Smartphone', 'Tablet', 'Laptop', 'Printer',
    'GitBranch', 'GitMerge', 'Package', 'Box',
    'Plug', 'Radio', 'Satellite', 'Router',
    'Mail', 'MessageSquare', 'Phone', 'Video',
    'Calendar', 'Clock', 'Timer', 'RefreshCw',
    'Download', 'Upload', 'Share2', 'Link',
    'Wallet', 'CreditCard', 'DollarSign', 'Receipt',
] as const

// ── Types ──

interface IconPickerProps {
    value: string
    onChange: (iconName: string) => void
}

// ── Helper: render a Lucide icon by name ──

export function LucideIcon({
    name,
    className = 'h-4 w-4',
    ...props
}: { name: string; className?: string } & React.HTMLAttributes<HTMLElement>) {
    const IconComponent = (LucideIcons as any)[name]
    if (!IconComponent) return null
    return <IconComponent className={className} {...props} />
}

// ── Component ──

export function IconPicker({ value, onChange }: IconPickerProps) {
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')
    const popoverRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!open) return
        const handleClick = (e: MouseEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target as HTMLElement)) {
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [open])

    const filtered = useMemo(() => {
        if (!search) return [...DOMAIN_ICONS]
        const q = search.toLowerCase()
        return DOMAIN_ICONS.filter(name => name.toLowerCase().includes(q))
    }, [search])

    return (
        <div className="relative">
            {/* Trigger */}
            <Button
                type="button"
                variant="outline"
                className="w-full justify-start gap-2 font-normal"
                onClick={() => setOpen(!open)}
            >
                <LucideIcon name={value || 'FileText'} className="h-4 w-4 shrink-0" />
                <span className="text-sm truncate">{value || 'Select icon…'}</span>
            </Button>

            {/* Popover */}
            {open && (
                <div
                    ref={popoverRef}
                    className="absolute left-0 top-full mt-1 w-72 bg-white border rounded-lg shadow-lg z-50 p-2"
                >
                    {/* Search */}
                    <div className="relative mb-2">
                        <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-gray-400" />
                        <Input
                            placeholder="Search icons…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-7 h-8 text-sm"
                            autoFocus
                        />
                    </div>

                    {/* Grid */}
                    <div className="grid grid-cols-8 gap-1 max-h-48 overflow-y-auto">
                        {filtered.map((name) => {
                            const isActive = value === name
                            return (
                                <button
                                    key={name}
                                    type="button"
                                    title={name}
                                    className={`
                                        flex items-center justify-center h-8 w-8 rounded
                                        transition-colors cursor-pointer
                                        ${isActive
                                            ? 'bg-blue-100 text-blue-700 ring-1 ring-blue-300'
                                            : 'hover:bg-gray-100 text-gray-600'
                                        }
                                    `}
                                    onClick={() => { onChange(name); setOpen(false); setSearch('') }}
                                >
                                    <LucideIcon name={name} className="h-4 w-4" />
                                </button>
                            )
                        })}
                        {filtered.length === 0 && (
                            <div className="col-span-8 text-center text-xs text-gray-400 py-4">
                                No icons match "{search}"
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}
